import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, Save, PawPrint, Weight, Hash, Palette, Calendar, User, Loader2 } from 'lucide-react';
import { Pet, Owner } from '../../types';
import toast from 'react-hot-toast';
import FormField from '../Form/FormField';
import { useFormValidation } from '../../hooks/useFormValidation';
import { compose, required, positive } from '../../utils/validators';

interface PetFormProps {
  pet?: Pet | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pet: Omit<Pet, 'id' | 'createdAt'>) => void;
  owners: Owner[];
}

const SPECIES = [
  { value: 'dog', label: 'Köpek', emoji: '🐕' },
  { value: 'cat', label: 'Kedi', emoji: '🐈' },
  { value: 'bird', label: 'Kuş', emoji: '🦜' },
  { value: 'rabbit', label: 'Tavşan', emoji: '🐰' },
  { value: 'other', label: 'Diğer', emoji: '🐾' },
];

const GENDERS = [
  { value: 'male', label: 'Erkek', icon: '♂' },
  { value: 'female', label: 'Dişi', icon: '♀' },
  { value: 'unknown', label: 'Bilinmiyor', icon: '?' },
];

const PetForm: React.FC<PetFormProps> = ({ pet, isOpen, onClose, onSubmit, owners }) => {
  const [submitting, setSubmitting] = useState(false);
  const [ageUnit, setAgeUnit] = useState<'months' | 'years'>('years');
  const [species, setSpecies] = useState<string>('dog');
  const [gender, setGender] = useState<string>('unknown');

  const form = useFormValidation(
    {
      name: '',
      breed: '',
      age: '',
      weight: '',
      color: '',
      owner_id: '',
    },
    {
      name: compose((v) => required(v, 'Hasta adı')),
      breed: compose((v) => required(v, 'Irk')),
      age: compose((v) => required(v, 'Yaş'), positive),
      weight: compose((v) => required(v, 'Kilo'), positive),
      owner_id: (v) => required(v, 'Sahip seçimi'),
    }
  );

  useEffect(() => {
    if (!isOpen) return;
    if (pet) {
      let displayAge: number | undefined = pet.age;
      let unit: 'years' | 'months' = 'months';
      if (pet.age >= 12) {
        displayAge = parseFloat((pet.age / 12).toFixed(1));
        unit = 'years';
      }
      form.setValues({
        name: pet.name || '',
        breed: pet.breed || '',
        age: (displayAge ?? '').toString(),
        weight: (pet.weight ?? '').toString(),
        color: pet.color || '',
        owner_id: pet.owner_id || '',
      });
      setSpecies(pet.species || 'dog');
      setGender(pet.gender || 'unknown');
      setAgeUnit(unit);
    } else {
      form.reset();
      setSpecies('dog');
      setGender('unknown');
      setAgeUnit('years');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validateAll()) {
      toast.error('Lütfen formdaki hataları düzeltin');
      return;
    }

    setSubmitting(true);
    try {
      const ageInMonths = ageUnit === 'years'
        ? Math.round(parseFloat(form.values.age) * 12)
        : parseFloat(form.values.age);

      const petData: any = {
        name: form.values.name.trim(),
        species,
        breed: form.values.breed,
        age: ageInMonths,
        weight: parseFloat(form.values.weight),
        color: form.values.color,
        gender,
        owner_id: form.values.owner_id || null,
      };
      await onSubmit(petData);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        className="dribbble-panel max-w-md w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-md shadow-amber-300/40">
              <PawPrint className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {pet ? 'Hasta Düzenle' : 'Yeni Hasta Ekle'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Tür seçici */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">Tür <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-5 gap-2">
                {SPECIES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSpecies(s.value)}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all ${
                      species === s.value
                        ? 'border-teal-500 bg-teal-50 shadow-md shadow-teal-200/40'
                        : 'border-gray-200 bg-white hover:border-teal-200 hover:bg-teal-50/30'
                    }`}
                  >
                    <span className="text-xl">{s.emoji}</span>
                    <span className={`text-[11px] font-semibold ${species === s.value ? 'text-teal-700' : 'text-gray-600'}`}>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <FormField
              label="Hasta Adı"
              name="name"
              icon={PawPrint}
              value={form.values.name}
              onChange={(v) => form.setValue('name', v)}
              onBlur={() => form.setFieldTouched('name')}
              error={form.errors.name}
              touched={form.touched.name}
              valid={!form.errors.name}
              placeholder="Pamuk"
              required
            />

            <FormField
              label="Irk"
              name="breed"
              value={form.values.breed}
              onChange={(v) => form.setValue('breed', v)}
              onBlur={() => form.setFieldTouched('breed')}
              error={form.errors.breed}
              touched={form.touched.breed}
              valid={!form.errors.breed}
              placeholder="Golden Retriever"
              required
            />

            {/* Yaş + Birim */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <FormField
                  label="Yaş"
                  name="age"
                  type="number"
                  icon={Calendar}
                  value={form.values.age}
                  onChange={(v) => form.setValue('age', v)}
                  onBlur={() => form.setFieldTouched('age')}
                  error={form.errors.age}
                  touched={form.touched.age}
                  valid={!form.errors.age}
                  placeholder="3"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">Birim</label>
                <select
                  value={ageUnit}
                  onChange={(e) => setAgeUnit(e.target.value as 'months' | 'years')}
                  className="w-full rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
                >
                  <option value="years">Yıl</option>
                  <option value="months">Ay</option>
                </select>
              </div>
            </div>

            <FormField
              label="Kilo (kg)"
              name="weight"
              type="number"
              icon={Weight}
              value={form.values.weight}
              onChange={(v) => form.setValue('weight', v)}
              onBlur={() => form.setFieldTouched('weight')}
              error={form.errors.weight}
              touched={form.touched.weight}
              valid={!form.errors.weight}
              placeholder="25.5"
              required
            />

            {/* Cinsiyet */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">Cinsiyet</label>
              <div className="grid grid-cols-3 gap-2">
                {GENDERS.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGender(g.value)}
                    className={`flex items-center justify-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                      gender === g.value
                        ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-teal-200'
                    }`}
                  >
                    <span className="text-lg">{g.icon}</span>
                    <span className="text-xs">{g.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <FormField
              label="Renk"
              name="color"
              icon={Palette}
              value={form.values.color}
              onChange={(v) => form.setValue('color', v)}
              placeholder="Beyaz, kahverengi vb."
              hint="Opsiyonel"
            />

            {/* Sahip seçici */}
            {owners.length === 0 ? (
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                <p className="font-semibold">Önce bir hasta sahibi eklemelisiniz</p>
                <p className="mt-0.5 text-amber-700">
                  Her hasta bir sahibe bağlıdır. "Hasta Sahipleri" sayfasından sahibi ekledikten sonra buraya dönün.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Sahibi <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.values.owner_id}
                  onChange={(e) => form.setValue('owner_id', e.target.value)}
                  onBlur={() => form.setFieldTouched('owner_id')}
                  className={`w-full rounded-xl border-2 px-3 py-2.5 text-sm bg-white outline-none focus:ring-4 transition-all ${
                    form.touched.owner_id && form.errors.owner_id
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                      : 'border-gray-200 focus:border-teal-400 focus:ring-teal-500/20'
                  }`}
                >
                  <option value="">Sahibini seçin...</option>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>{o.name} {o.phone ? `(${o.phone})` : ''}</option>
                  ))}
                </select>
                {form.touched.owner_id && form.errors.owner_id && (
                  <p className="text-xs text-red-600 font-medium">{form.errors.owner_id}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-gray-200 bg-white shrink-0 rounded-b-2xl">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={submitting}>
              İptal
            </button>
            <button type="submit" disabled={submitting || !form.isValid} className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Kaydediliyor...</>
              ) : (
                <><Save className="w-4 h-4" /> {pet ? 'Güncelle' : 'Kaydet'}</>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>,
    document.body
  );
};

export default PetForm;
